/**
 * Utility functions for maintenance data normalization
 */

/**
 * Normalize maintenance history from various legacy formats
 * @param {any} src - Raw data from storage
 * @returns {Array} Always returns an array
 */
export function normalizeHistory(src) {
  // Already an array - most common case
  if (Array.isArray(src)) return src;
  
  // Legacy wrapper format: { history: [...] }
  if (Array.isArray(src?.history)) return src.history;
  
  // Object with nested arrays: { "item-1": [...], "item-2": [...] }
  if (src && typeof src === 'object') {
    const vals = Object.values(src);
    // Flatten if values are arrays
    if (vals.every(v => Array.isArray(v))) {
      return vals.flat();
    }
    // Direct object values if they look like records
    if (vals.length && typeof vals[0] === 'object') {
      return vals;
    }
  }
  
  // Fallback to empty array

  return [];
}

/**
 * Check if value needs migration to array format
 */
export function needsMigration(v) {
  if (Array.isArray(v)) return false;
  if (v && Array.isArray(v.history)) return true; // wrapper form
  if (v && typeof v === 'object') return true;    // legacy object map form
  return false;
}

/**
 * Helper to ensure every record has a stable ID
 * @param {Object} record - The record to check
 * @returns {string} A stable ID for the record
 */
export function ensureId(record) {
  return record?.id ?? 
         record?.checkId ?? 
         `${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

/**
 * Safely convert to array format
 */
export function toArray(v) {
  if (Array.isArray(v)) return v;
  if (Array.isArray(v?.history)) return v.history;
  return [];
}

/**
 * Safe map function that always works on arrays
 */
export function safeMap(data, fn) {
  const arr = toArray(normalizeHistory(data));
  return arr.map(fn);
}

/**
 * Shallow compare two arrays for equality
 */
export function shallowEqualArrays(a, b) {
  return Array.isArray(a) && 
         Array.isArray(b) && 
         a.length === b.length && 
         a.every((v, i) => v === b[i]);
}

/**
 * Normalize record fields to canonical shape
 * Always ensures a stable ID
 */
export function normalizeRecord(r) {
  return {
    id: ensureId(r),  // ALWAYS have a stable ID
    itemId: r.itemId ?? r.equipmentId ?? null,
    type: r.type ?? r.checkType ?? 'inspection',
    date: r.date ?? r.completedDate ?? null,
    status: r.status ?? r.state ?? 'done',
    notes: r.notes ?? r.comment ?? ''
  };
}

/**
 * Get default performer name based on current user and vessel
 * @param {Object} currentUser - Current user object
 * @param {Object} vessel - Current vessel object
 * @returns {string} Default performer name
 */
export function getDefaultPerformer(currentUser, vessel) {
  if (currentUser?.displayName) return currentUser.displayName;
  if (currentUser?.name) return currentUser.name;
  return `${vessel?.name ?? 'Vessel'} Crew`;
}